/// <amd-module name="Wit/WorkItemForm/Common/Components/CollapsibleSection" />
import * as React from "react";
import { PropsWithChildren, useContext, useEffect, useState } from "react";
import { IObservableValue, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { useService } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Button } from "VSSUI/Button";
import { Icon, IIconProps } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId } from "VSSUI/Util";
import * as Resources from "Wit/WorkItemForm/Resources";
import { WorkItemFormPageContext } from "../Hooks";

const collapsedSectionsSetting = "WorkItemForm/CollapsedSections";
export const collapseButtonClassName = "work-item-form-toggle-collapse";
export const collapsibleSectionClassName = "work-item-form-collapsible-section";

export const FullScreenSectionContext = React.createContext<{
    setFullScreenContent: (expandedContent: JSX.Element | undefined) => void;
    fullScreenContent: JSX.Element | undefined;
}>({ setFullScreenContent: () => {}, fullScreenContent: undefined });

export function FullScreenSectionContainer({ children }: PropsWithChildren<{}>) {
    const [fullScreenContent, setFullScreenContent] = useState<JSX.Element | undefined>();

    return (
        <div className="work-item-form-expanded-section-container v-scroll-auto full-height flex-row">
            <FullScreenSectionContext.Provider value={{ setFullScreenContent, fullScreenContent }}>
                <div className={`work-item-from-fullscreen-content full-width padding-8 ${fullScreenContent ? "" : "hidden"}`}>
                    {fullScreenContent}
                </div>
                <div className={fullScreenContent ? "hidden" : "scroll-hidden full-width"}>{children}</div>
            </FullScreenSectionContext.Provider>
        </div>
    );
}

export interface ICollapsibleSectionProps {
    disabled?: boolean;
    hasHtmlControl?: boolean;
    tooltipText?: string;
    iconProps?: IIconProps | IObservableValue<IIconProps | undefined>;
    label: string;
    settingsKey?: string;
    sectionId?: string;
}

export function CollapsibleSection({
    children,
    disabled,
    hasHtmlControl,
    tooltipText,
    iconProps,
    label,
    settingsKey,
    sectionId
}: PropsWithChildren<ICollapsibleSectionProps>) {
    const { setFullScreenContent, fullScreenContent } = useContext(FullScreenSectionContext);
    const [focusFullScreenButton, setFocusFullScreenButton] = useState(false);

    const settingsService = useService<ISettingsService>("ISettingsService");
    const getInitialCollapsedState = () =>
        settingsKey ? !!settingsService.getEntry<{ [key: string]: boolean }>(collapsedSectionsSetting, SettingsUserScope.Me)?.[settingsKey] : false;

    const [collapsed, _setCollapsed] = useState(() => getInitialCollapsedState());
    const setCollapsed = (collapsed: boolean) => {
        if (settingsKey) {
            const entries = { [`${collapsedSectionsSetting}/${settingsKey}`]: collapsed };
            settingsService.setEntries(entries, SettingsUserScope.Me);
        }
        _setCollapsed(collapsed);
    };

    const workItemFormContext = useContext(WorkItemFormPageContext);

    useEffect(() => {
        // Only restore on discussion view
        if (settingsKey?.indexOf("Discussion-Section") === -1) {
            return;
        }

        const restoreDiscussionView = () => {
            if (workItemFormContext.focusDiscussion) {
                if (fullScreenContent) {
                    setFullScreenContent(undefined);
                }
                if (collapsed) {
                    setCollapsed(false);
                }
            }
        };

        restoreDiscussionView();
    }, [workItemFormContext.focusDiscussion]);

    useEffect(() => {
        // Only dispatch the event for discussion section
        if (settingsKey?.indexOf("Discussion-Section") === -1) {
            return;
        }

        if (!collapsed && !fullScreenContent && workItemFormContext.focusDiscussion) {
            const focusDiscussionEvent = new Event("wif-focus-discussion");
            document.dispatchEvent(focusDiscussionEvent);
        }
    });

    const collapsibleSectionFullScreenContent = disabled ? undefined : (
        <CollapsibleSectionDesign
            sectionId={sectionId}
            collapsed={false}
            disabled={false}
            fullScreen={true}
            hasHtmlControl={hasHtmlControl}
            tooltipText={tooltipText}
            iconProps={iconProps}
            label={label}
            onRestore={() => {
                setFocusFullScreenButton(true);
                setFullScreenContent(undefined);
            }}
            onToggleCollapsed={() => {}}
        >
            {children}
        </CollapsibleSectionDesign>
    );

    return (
        <CollapsibleSectionDesign
            sectionId={sectionId}
            autoFocusFullScreenButton={focusFullScreenButton}
            collapsed={!disabled && collapsed}
            disabled={disabled}
            fullScreen={false}
            hasHtmlControl={hasHtmlControl}
            tooltipText={tooltipText}
            iconProps={iconProps}
            label={label}
            onFullScreen={() => {
                setFocusFullScreenButton(false);
                setFullScreenContent(collapsibleSectionFullScreenContent);
            }}
            onToggleCollapsed={disabled ? () => {} : () => setCollapsed(!collapsed)}
        >
            {children}
        </CollapsibleSectionDesign>
    );
}

interface ICollapsibleSectionDesignProps extends Omit<ICollapsibleSectionProps, "settingsKey"> {
    autoFocusFullScreenButton?: boolean;
    collapsed: boolean;
    fullScreen: boolean;
    onFullScreen?: () => void;
    onRestore?: () => void;
    onToggleCollapsed: () => void;
}

function CollapsibleSectionDesign({
    autoFocusFullScreenButton,
    label,
    collapsed,
    disabled,
    hasHtmlControl,
    tooltipText,
    iconProps,
    fullScreen,
    onFullScreen,
    onRestore,
    onToggleCollapsed,
    children
}: PropsWithChildren<ICollapsibleSectionDesignProps>) {
    const expandCollapseButtonLabel = format(collapsed ? Resources.ExpandSectionLabel : Resources.CollapseSectionLabel, label);
    const maximizeButtonLabel = format(Resources.MaximizeSectionLabel, label);
    const restoreButtonLabel = format(Resources.RestoreSectionLabel, label);
    const fullScreenButtonRef = React.useRef(React.createRef<Button>());
    const [focusFullScreenButton, setFocusFullScreenButton] = useState(false);
    const htmlId = getSafeId(label + Date.now());

    useEffect(() => {
        if (autoFocusFullScreenButton && focusFullScreenButton && fullScreenButtonRef.current.current) {
            fullScreenButtonRef.current.current.focus();
            setFocusFullScreenButton(false);
        }
    }, [autoFocusFullScreenButton, focusFullScreenButton, fullScreenButtonRef.current]);

    const themeService: IVssThemeService = useService("IVssThemeService");
    const [showDarkThemeFormat, setShowDarkThemeFormat] = useState(false);
    const showFormattingToggleAriaLabel = showDarkThemeFormat ? Resources.HideDarkThemeFormat : Resources.ShowDarkThemeFormat;

    return (
        <Observer themeService={themeService}>
            {() => {
                const isDark = themeService.getCurrentTheme().isDark;
                return (
                    <div
                        className={css(
                            collapsibleSectionClassName,
                            disabled && "disabled",
                            hasHtmlControl && !showDarkThemeFormat && isDark && "work-item-form-section-dark-theme-override"
                        )}
                    >
                        <div className="work-item-form-collapsible-header margin-bottom-4 flex-row justify-space-between" role="button" aria-controls={htmlId} onClick={onToggleCollapsed} aria-expanded={!collapsed}>
                            <span className="font-size-ml font-weight-semibold" aria-level={2} role="heading">
                                {tooltipText ? (<Tooltip className="work-item-form-control-tooltip" text={tooltipText}>
                                    <div>{label}</div>
                                </Tooltip>) : label}
                                <Observer iconProps={iconProps}>
                                    {(observerProps: { iconProps?: IIconProps }) => {
                                        return observerProps.iconProps ? <Icon {...observerProps.iconProps} /> : null;
                                    }}
                                </Observer>
                            </span>
                            <div className="work-item-form-section-controls">
                                {fullScreen && !disabled && !!onRestore && (
                                    <Button
                                        autoFocus={true}
                                        ariaLabel={restoreButtonLabel}
                                        subtle
                                        iconProps={{ iconName: "BackToWindow" }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onRestore();
                                        }}
                                    />
                                )}

                                {!fullScreen && !disabled && !!onFullScreen && (
                                    <Button
                                        ref={fullScreenButtonRef.current}
                                        ariaLabel={maximizeButtonLabel}
                                        className="work-item-form-toggle"
                                        subtle
                                        iconProps={{ iconName: "FullScreen" }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onFullScreen();
                                            // Focus the fullscreen button the next time it renders.
                                            setFocusFullScreenButton(true);
                                        }}
                                    />
                                )}

                                {!fullScreen && !disabled && (
                                    <Button
                                        ariaExpanded={!collapsed}
                                        ariaLabel={expandCollapseButtonLabel}
                                        className={css(collapseButtonClassName, "work-item-form-toggle", collapsed && "collapsed")}
                                        subtle
                                        iconProps={{ iconName: collapsed ? "ChevronDown" : "ChevronUp" }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onToggleCollapsed();
                                        }}
                                    />
                                )}

                                {hasHtmlControl && isDark && (
                                    <Button
                                        className="work-item-form-toggle"
                                        iconProps={showDarkThemeFormat ? { iconName: "Hide2" } : { iconName: "RedEye" }}
                                        ariaLabel={showFormattingToggleAriaLabel}
                                        tooltipProps={{
                                            text: showFormattingToggleAriaLabel
                                        }}
                                        subtle
                                        onClick={e => {
                                            e.stopPropagation();
                                            setShowDarkThemeFormat(current => !current);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                        <div role="region" id={htmlId} aria-hidden={collapsed} className={css("work-item-form-collapsible-section-content", collapsed && "collapsed")}>
                            {children}
                        </div>
                    </div>
                );
            }}
        </Observer>
    );
}
